"use client";

import Script from "next/script";
import { useEffect, useRef, useState } from "react";
import type { ReactElement } from "react";

const WIDGET_SRC: string = "https://jobs-widget.recruiteecdn.com/widget.js";

declare global {
  interface Window {
    RTWidget?: new (config: Record<string, unknown>) => unknown;
  }
}

/**
 * Recruitee jobs widget. Script stays off the network until the panel is
 * near the viewport, so it never fights the hero for bandwidth.
 */
export function CareersRecruiteeEmbed({
  config,
}: {
  readonly config: Record<string, unknown>;
}): ReactElement {
  const hostRef = useRef<HTMLDivElement>(null);
  const [shouldLoad, setShouldLoad] = useState<boolean>(false);

  useEffect(() => {
    const host: HTMLDivElement | null = hostRef.current;
    if (!host) return;
    const observer: IntersectionObserver = new IntersectionObserver(
      (entries: IntersectionObserverEntry[]) => {
        const entry: IntersectionObserverEntry | undefined = entries[0];
        if (entry === undefined || !entry.isIntersecting) return;
        setShouldLoad(true);
        observer.disconnect();
      },
      { rootMargin: "480px 0px" },
    );
    observer.observe(host);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!shouldLoad) return;
    let cancelled = false;
    const tryInit = (): void => {
      if (cancelled) return;
      if (window.RTWidget) {
        new window.RTWidget(config);
        return;
      }
      window.setTimeout(tryInit, 100);
    };
    tryInit();
    return () => {
      cancelled = true;
    };
  }, [shouldLoad, config]);

  return (
    <div className="c2-jobs-panel" ref={hostRef}>
      <div id="recruitee-careers" className="careers-jobs-embed" />
      {shouldLoad ? <Script src={WIDGET_SRC} strategy="lazyOnload" /> : null}
    </div>
  );
}
